import React from 'react';
import { Database, RefreshCw } from 'lucide-react';
import { ArtifactRenderer } from './Artifacts';

/** Les balises qui ne peuvent pas vivre à l'intérieur d'un `<p>`. */
const BLOCS = 'h[2-4]|ul|ol|table|pre|blockquote|hr';

/** Le jeton qui tient la place d'un bloc de code le temps du formatage. */
const JETON_CODE = (i) => `KOKPITBLOCCODE${i}FIN`;

/**
 * Convertit un tableau markdown en `<table>`.
 *
 * La ligne de séparation (`|---|---|`) distingue l'en-tête du corps ; elle est obligatoire en
 * markdown et les modèles la produisent systématiquement. Si elle manque, tout part dans le
 * corps plutôt que de perdre le tableau.
 */
const construireTableau = (bloc) => {
  const cellules = (ligne) => ligne.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
  const lignes = bloc.trim().split('\n').filter((l) => l.trim());
  const estSeparateur = (l) => /^\|?[\s:|-]*-[\s:|-]*\|?$/.test(l.trim());

  const iSep = lignes.findIndex(estSeparateur);
  const entete = iSep > 0 ? lignes.slice(0, iSep) : [];
  const corps = lignes.slice(iSep >= 0 ? iSep + 1 : 0);

  const th = entete.map((l) => `<tr>${cellules(l).map((c) => `<th>${c}</th>`).join('')}</tr>`).join('');
  const td = corps.map((l) => `<tr>${cellules(l).map((c) => `<td>${c}</td>`).join('')}</tr>`).join('');

  return `<table>${th ? `<thead>${th}</thead>` : ''}<tbody>${td}</tbody></table>`;
};

/**
 * Formate le texte markdown basique en HTML.
 *
 * **L'ordre des règles n'est pas interchangeable**, et c'est tout l'enjeu de cette fonction :
 *
 * 1. **Les blocs de code sont mis de côté en premier.** Sans cela, la règle du code *en ligne*
 *    (`` `…` ``) dévorait une partie des accents graves d'ouverture et laissait le reste à
 *    l'écran, puis grisait chaque ligne du bloc séparément — l'affichage en escalier observé.
 * 2. **Les tableaux avant le découpage en paragraphes.** Leurs lignes commencent par `|` et
 *    s'affichaient telles quelles, barres verticales et ligne de tirets comprises.
 * 3. **Les titres et les puces avant le gras.** Une fois `**…**` devenu `<strong>`, la ligne ne
 *    commence plus par `**` et le titre est manqué.
 *
 * Les citations se reconnaissent à `&gt;` et non à `>` : l'échappement HTML a déjà eu lieu.
 */
const formatMessage = (content) => {
  if (!content) return '';

  let texte = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // 1. Blocs de code, mis à l'abri derrière un jeton que rien d'autre ne reconnaît.
  const blocsCode = [];
  texte = texte.replace(/```[a-zA-Z0-9+-]*\n?([\s\S]*?)```/g, (_, code) => {
    blocsCode.push(code.replace(/\n+$/, ''));
    return `\n\n${JETON_CODE(blocsCode.length - 1)}\n\n`;
  });
  // Un accent grave triple resté seul — bloc jamais refermé — ne doit pas s'afficher.
  texte = texte.replace(/```[a-zA-Z0-9+-]*/g, '');

  // 2. Tableaux : au moins deux lignes consécutives encadrées de barres verticales.
  texte = texte.replace(/(?:^[ \t]*\|.*\|[ \t]*$\n?){2,}/gm, (bloc) => `\n${construireTableau(bloc)}\n`);

  texte = texte
    // 3. Filets de séparation
    .replace(/^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, '<hr/>')
    // 4. Titres markdown
    .replace(/^#{4,6} (.+)$/gm, '<h4>$1</h4>')
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    // Une ligne entièrement en gras tient lieu de titre de section
    .replace(/^[ \t]*\*\*(.+?)\*\*[ \t]*:?[ \t]*$/gm, '<h4>$1</h4>')
    // 5. Citations
    .replace(/^[ \t]*&gt;[ \t]?(.*)$/gm, '<blockquote>$1</blockquote>')
    // 6. Listes : tiret, puce ou numéro
    .replace(/^[ \t]*[-–—•·][ \t]+(.+)$/gm, '<li>$1</li>')
    // La puce numérotée se marque, pour que le regroupement rende un `<ol>` : sans cela, une
    // liste d'étapes « 1. 2. 3. » perdait sa numérotation en chemin.
    .replace(/^[ \t]*\d+\.[ \t]+(.+)$/gm, '<li data-ordre>$1</li>')
    // 7. Enrichissements en ligne
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*\n]+)\*/g, '<em>$1</em>')
    .replace(/`([^`\n]+)`/g, '<code>$1</code>');

  /*
   * Regrouper les `<li>` *consécutifs* en une seule liste, et de même pour les citations.
   *
   * La version d'origine capturait `(<li>.*</li>)` en mode glouton : la première et la dernière
   * puce du message, et tout ce qu'il y avait entre les deux — titres compris — se retrouvaient
   * enfermés dans un seul `<ul>`.
   */
  texte = texte
    .replace(/<li(?: data-ordre)?>[\s\S]*?<\/li>(?:\s*<li(?: data-ordre)?>[\s\S]*?<\/li>)*/g, (bloc) => {
      const items = bloc.replace(/\s*\n\s*/g, '');
      // Une liste est numérotée si elle commence par un élément numéroté : on ne mélange pas
      // les deux formes, et c'est le premier qui donne le ton.
      const balise = items.startsWith('<li data-ordre>') ? 'ol' : 'ul';
      return `<${balise}>${items.replace(/ data-ordre/g, '')}</${balise}>`;
    })
    .replace(/<blockquote>[\s\S]*?<\/blockquote>(?:\n<blockquote>[\s\S]*?<\/blockquote>)*/g,
      (bloc) => `<blockquote>${bloc.replace(/<\/?blockquote>/g, '').split('\n').filter((l) => l.trim()).join('<br/>')}</blockquote>`);

  /*
   * Paragraphes : on découpe sur les lignes vides, et **on n'enferme pas un bloc dans un `<p>`**.
   *
   * Un `<h4>`, un `<ul>` ou un `<table>` ne peut pas vivre dans un paragraphe : le navigateur
   * ferme celui-ci de lui-même et laisse un `<p>` vide derrière, dont la marge s'ajoutait
   * silencieusement à celle du titre.
   */
  const motifBloc = new RegExp(
    `(<(?:${BLOCS})\\b[\\s\\S]*?</(?:${BLOCS})>|<hr/>|KOKPITBLOCCODE\\d+FIN)`
  );
  const debutDeBloc = new RegExp(`^(?:<(?:${BLOCS})\\b|<hr/>|KOKPITBLOCCODE\\d+FIN$)`);
  texte = texte
    .split(/\n{2,}/)
    .map((bloc) => {
      const contenu = bloc.trim();
      if (!contenu) return '';
      // Un même paragraphe peut mêler du texte et un bloc — « Puis : » suivi d'une liste. On
      // découpe sur les blocs et on n'enveloppe que ce qui est réellement du texte courant.
      return contenu
        .replace(/\n/g, '<br/>')
        .split(motifBloc)
        .map((morceau) => {
          const t = morceau ? morceau.trim() : '';
          if (!t) return '';
          /*
           * Seule une **balise de bloc** échappe au paragraphe.
           *
           * Le test portait sur un `<` quelconque : un paragraphe qui commence par du gras —
           * « **Ordinateur** domine… », la forme la plus courante d'une énumération — était pris
           * pour un bloc et sortait nu, donc sans marge. Il se collait au suivant, ce qui donnait
           * le pavé continu qu'on voyait à l'écran.
           */
          return debutDeBloc.test(t) ? t : `<p>${t}</p>`;
        })
        .join('');
    })
    .join('');

  // Un `<br/>` accolé à un bloc, ou en bout de paragraphe, ajoute une ligne vide que personne
  // n'a demandée.
  texte = texte
    .replace(new RegExp(`<br/>\\s*(?=<(?:${BLOCS})\\b)`, 'g'), '')
    .replace(new RegExp(`(</(?:${BLOCS})>|<hr/>)\\s*<br/>`, 'g'), '$1')
    .replace(/(?:<br\/>\s*)+<\/p>/g, '</p>')
    .replace(/<p>(?:\s*<br\/>)+/g, '<p>')
    .replace(/<p>\s*<\/p>/g, '');

  // 8. Les blocs de code reprennent leur place, intacts.
  return texte.replace(/KOKPITBLOCCODE(\d+)FIN/g, (_, i) => `<pre><code>${blocsCode[Number(i)]}</code></pre>`);
};

/**
 * Formate la date relative
 */
const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;

  if (diff < 60000) return "À l'instant";
  if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
  if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)} h`;

  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * ChatMessage - Affiche un message dans la conversation
 */
const ChatMessage = ({ message }) => {
  const isUser = message.role === 'USER';

  return (
    <div className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
      {/* Pas d'avatar : dans un panneau d'un tiers d'écran, deux pastilles de 32 px plus leur
          gouttière coûtaient 44 px de largeur de lecture de chaque côté, pour une information
          que l'alignement et la couleur de la bulle donnent déjà. */}

      {/* Content */}
      <div className="chat-message-content">
        {/* Text */}
        <div
          className="chat-message-text"
          dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
        />

        {/* Meta info pour les messages assistant */}
        {!isUser && (
          <div className="chat-message-meta">
            {message.usedExistingData ? (
              <span className="meta-tag context">
                <Database size={10} />
                Données existantes
              </span>
            ) : message.dataRowCount !== null && message.dataRowCount !== undefined ? (
              <span className="meta-tag new-query">
                <RefreshCw size={10} />
                {/* Zéro reste un résultat : « 0 nouvelle ligne » dit que la régie a été
                    interrogée et n'a rien, ce qu'une absence d'étiquette tairait. */}
                {message.dataRowCount} nouvelle{message.dataRowCount > 1 ? 's' : ''} ligne{message.dataRowCount > 1 ? 's' : ''}
              </span>
            ) : null}

            {message.tokensUsed > 0 && (
              <span className="meta-tag tokens">
                {message.tokensUsed} tokens
              </span>
            )}

            {/*
              * Le coût, à côté des jetons.
              *
              * Un nombre de jetons ne dit rien à qui ne connaît pas les tarifs — et c'est le
              * coût qui a surpris dans cette application, pas le décompte. Quatre décimales
              * parce qu'un échange coûte des centimes : arrondir au centime afficherait « 0,00 $ »
              * sur la moitié des messages, ce qui se lirait comme « gratuit ».
              */}
            {message.estimatedCost > 0 && (
              <span className="meta-tag cout" title="Coût estimé de cet échange">
                ≈ {message.estimatedCost < 0.0001 ? '<0,0001' : message.estimatedCost.toFixed(4).replace('.', ',')} $
              </span>
            )}

            <span className="meta-time">
              {formatTime(message.createdAt)}
            </span>
          </div>
        )}

        {/* Reasoning (si disponible) */}
        {!isUser && message.reasoning && (
          <div className="chat-message-reasoning">
            <em>{message.reasoning}</em>
          </div>
        )}

        {/* Artifacts (graphiques, tableaux) */}
        {!isUser && message.artifacts && message.artifacts.length > 0 && (
          <div className="chat-message-artifacts">
            {message.artifacts.map((artifact, idx) => (
              <div key={idx} className="chat-artifact-wrapper">
                <ArtifactRenderer artifact={artifact} />
              </div>
            ))}
          </div>
        )}

        {/* GAQL Query (si disponible et nouvelle requête) */}
        {!isUser && message.gaqlQuery && (
          <details className="chat-message-gaql">
            <summary>Voir la requête GAQL</summary>
            <pre><code>{message.gaqlQuery}</code></pre>
          </details>
        )}
      </div>
    </div>
  );
};

export { formatMessage };
export default ChatMessage;
